import React, {
  createContext,
  useReducer,
  useContext,
  useCallback,
  useMemo,
  ReactNode,
} from 'react'
import OnboardingDrawer from '../../onboardingDrawer'

enum DrawerIds {
  Onboarding = 'onboarding',
}

const ApplicationDrawerContext = createContext({
  open: (_id: string, _props: any) => {},
  close: () => {},
  drawerIds: DrawerIds,
})

const applicationDrawerReducer = (state: any, action: any) => {
  switch (action.type) {
    case 'OPEN_ACTIVE_DRAWER': {
      return {
        ...state,
        activeDrawerId: action.id,
        drawerProps: action.drawerProps || {},
      }
    }

    case 'CLOSE_ACTIVE_DRAWER': {
      return {
        ...state,
        activeDrawerId: null,
      }
    }
    default: {
      return state
    }
  }
}

const ApplicationDrawersProvider = ({
  children,
  onboardingProps,
}: {
  children: ReactNode
  onboardingProps?: any
}) => {
  const enabled = onboardingProps?.enabled
  const [state, dispatch] = useReducer(applicationDrawerReducer, {
    // TODO: Find a way to enforce only extending from the default drawer instances we define...
    drawerInstances: {},
    activeDrawerId: enabled ? DrawerIds.Onboarding : null,
    drawerProps: {
      ...(onboardingProps || {}),
      close: () => dispatch({ type: 'CLOSE_ACTIVE_DRAWER' }),
    },
  })

  const open = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (id: string, drawerProps: any) => {
      dispatch({ type: 'OPEN_ACTIVE_DRAWER', id, drawerProps })
    },
    [dispatch]
  )

  const close = useCallback(() => {
    if (!state.activeDrawerId) return
    dispatch({ type: 'CLOSE_ACTIVE_DRAWER' })
  }, [state.drawerInstances, state.activeDrawerId, dispatch])

  const context = useMemo(
    () => ({
      open,
      close,
      drawerIds: DrawerIds,
    }),
    [close, open]
  )

  const getSelectedDrawer = () => {
    switch (state.activeDrawerId) {
      case DrawerIds.Onboarding:
        return (
          <OnboardingDrawer
            close={close}
            elmId='onboarding_drawer'
            {...state.drawerProps}
          />
        )
      default:
        return null
    }
  }
  return (
    <ApplicationDrawerContext.Provider value={context}>
      {getSelectedDrawer()}
      {children}
    </ApplicationDrawerContext.Provider>
  )
}

const useApplicationDrawers = () => {
  const context = useContext(ApplicationDrawerContext)

  // TODO: Do something here if the context provider hasn't already been provided. We should have a standard pattern for
  // what that means which we don't have right now.

  return context
}

export {
  ApplicationDrawerContext,
  ApplicationDrawersProvider,
  useApplicationDrawers,
}
